// A single deck page's platform-map matrix, rendered at a FIXED global scale on
// a UNION grid so labels never move and the scale never drifts page-to-page.
//
// Reuses the live matrix computations verbatim (buildRatingIndex /
// buildSkillFunctions / buildDerivedContextFunctionGate / makeMergedCoverageCell /
// MatrixCell) so a cell here is pixel-identical to the Timeline's grouped
// (skills-collapsed) coverage cell. What differs from PlatformMapMatrix:
//   · the grid template is fixed px (from DeckLayout.scale), not `1fr` auto-fit;
//   · rows/columns come from the shared UNION order, filtered per page to the
//     entries that exist then (never-existed are hidden; retired ones render
//     with the live deprecate treatment — see deck-layout.ts / DeckPageAxes).
// The live TimelineView / PlatformMapMatrix are left untouched.

import type { TimelineSnapshot } from '@lib/api-types/views';
import { useMemo } from 'react';

import {
  buildDerivedContextFunctionGate,
  buildRatingIndex,
  buildSkillFunctions,
  flatColumns,
  indexSkills,
  isDefaultVisibleSkill,
  TALENT_LEVELS,
  type DerivedContext,
  type Engineer,
  type RatingValue,
  type RollupColumn,
} from '../data-views.ts';
import type { DeckLayout, DeckPageAxes } from '../deck-layout.ts';
import { makeMergedCoverageCell } from '../matrix-cells-views.ts';

import { MatrixCell } from './MatrixCell.tsx';

interface DeckMatrixProps {
  snapshot: TimelineSnapshot;
  layout: DeckLayout;
  axes: DeckPageAxes;
  /** Coverage threshold — an engineer "covers" a cell at ≥ this rating. */
  talentLevel?: RatingValue;
}

/** Renders one page's fixed-scale union matrix. */
export function DeckMatrix({ snapshot, layout, axes, talentLevel = 2 }: DeckMatrixProps) {
  const { scale, bfDisplay, compDisplay, compSkillCount } = layout;

  // Present (skills-collapsed) derivedContexts of THIS snapshot — the active rows.
  const presentComps = useMemo(
    () =>
      snapshot.derivedContexts
        .map((c) => ({ ...c, skills: c.skills.filter(isDefaultVisibleSkill) }))
        .filter((c) => c.skills.length > 0),
    [snapshot.derivedContexts],
  );
  const compById = useMemo(
    () => new Map(presentComps.map((c) => [c.id, c])),
    [presentComps],
  );

  const fns = snapshot.business_functions;
  const team = useMemo(() => snapshot.engineers.filter((e) => e.kind === 'human'), [snapshot.engineers]);
  const skillsById = useMemo(() => indexSkills(presentComps), [presentComps]);
  const skillFunctions = useMemo(() => buildSkillFunctions(presentComps), [presentComps]);
  const ratingIndex = useMemo(
    () => buildRatingIndex(skillsById, team, snapshot.ratings),
    [skillsById, team, snapshot.ratings],
  );
  // Every column here is `plain` (see `colByFn` below), so the gate's
  // parent-remapped map is never consulted — the deck's union axis comes from
  // `comp.functions` edges directly and knows nothing about the hierarchy.
  const cfGate = useMemo(
    () => buildDerivedContextFunctionGate(presentComps, fns),
    [presentComps, fns],
  );
  const colByFn = useMemo(() => {
    const m = new Map<string, RollupColumn>();
    for (const col of flatColumns(fns)) m.set(col.fn.id, col);
    return m;
  }, [fns]);
  const levelLabel = TALENT_LEVELS.find((l) => l.key === talentLevel)?.label ?? '';

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `${scale.compLabelPx}px repeat(${axes.visibleBfIds.length}, ${scale.cellPx}px)`,
      gridTemplateRows: `${scale.headerPx}px repeat(${axes.visibleCompIds.length}, ${scale.rowPx}px)`,
      gap: `${scale.gapPx}px`,
    }),
    [scale, axes.visibleBfIds.length, axes.visibleCompIds.length],
  );

  if (axes.visibleBfIds.length === 0 || axes.visibleCompIds.length === 0) return null;

  return (
    <div className='snap-grid snap-grid--deck' style={gridStyle}>
      {/* single empty corner cell above the derivedContext-label column */}
      <div className='snap-grid__col-header' />
      {axes.visibleBfIds.map((id) => {
        const st = axes.bfState.get(id);
        const mod =
          st === 'deprecated'
            ? ' snap-grid__col-header--deprecated'
            : axes.newBfIds.has(id)
              ? ' snap-grid__col-header--new'
              : '';
        // BF names wrap to two lines (no truncation) — see .snap-grid--deck rules.
        return (
          <div key={id} className={`snap-grid__col-header${mod}`}>
            <strong>{bfDisplay.get(id) ?? id}</strong>
          </div>
        );
      })}

      {axes.visibleCompIds.map((compId) => {
        const st = axes.compState.get(compId);
        const compRemoved = st === 'deprecated';
        const compNew = axes.newCompIds.has(compId);
        const nameMod = compRemoved
          ? ' deck-comp-label--deprecated'
          : compNew
            ? ' deck-comp-label--new'
            : '';
        const comp = compById.get(compId);
        return (
          <DeckRow
            key={compId}
            comp={comp}
            display={compDisplay.get(compId) ?? compId}
            skillCount={compSkillCount.get(compId) ?? 0}
            nameMod={nameMod}
            compRemoved={compRemoved}
            compNew={compNew}
            axes={axes}
            cfGate={cfGate}
            colByFn={colByFn}
            ratingIndex={ratingIndex}
            skillFunctions={skillFunctions}
            team={team}
            threshold={talentLevel}
            levelLabel={levelLabel}
          />
        );
      })}
    </div>
  );
}

interface DeckRowProps {
  comp: DerivedContext | undefined; // undefined when the row is retired (not in this snapshot)
  display: string;
  skillCount: number;
  nameMod: string;
  compRemoved: boolean;
  compNew: boolean;
  axes: DeckPageAxes;
  cfGate: ReturnType<typeof buildDerivedContextFunctionGate>;
  colByFn: Map<string, RollupColumn>;
  ratingIndex: ReturnType<typeof buildRatingIndex>;
  skillFunctions: ReturnType<typeof buildSkillFunctions>;
  team: Engineer[];
  threshold: RatingValue;
  levelLabel: string;
}

/** One derivedContext row: a stacked label cell (name + count) + union BF cells. */
function DeckRow({
  comp,
  display,
  skillCount,
  nameMod,
  compRemoved,
  compNew,
  axes,
  cfGate,
  colByFn,
  ratingIndex,
  skillFunctions,
  team,
  threshold,
  levelLabel,
}: DeckRowProps) {
  return (
    <>
      {/* One label cell: full derivedContext name, skill count stacked beneath. */}
      <div className={`deck-comp-label${nameMod}`}>
        <span className='deck-comp-label__name'>{display}</span>
        <span className='deck-comp-label__count'>{skillCount} skills</span>
      </div>
      {axes.visibleBfIds.map((bfId) => {
        const bfRemoved = axes.bfState.get(bfId) === 'deprecated';
        // Retired row or column → historical striped cell (value hidden by CSS).
        if (compRemoved || bfRemoved) {
          return <MatrixCell key={bfId} value={null} levelClass='na' extraClass='is-merged is-deprecated' />;
        }
        const col = comp ? colByFn.get(bfId) : undefined;
        if (!comp || !col || !cfGate.serves(comp.id, col)) {
          return <MatrixCell key={bfId} value={null} levelClass='na' extraClass='is-merged' />;
        }
        const cell = makeMergedCoverageCell(
          comp,
          col.fn,
          threshold,
          levelLabel,
          ratingIndex,
          skillFunctions,
          team,
        );
        const isNew = compNew || axes.newBfIds.has(bfId);
        const extra = `is-merged${isNew ? ' is-new' : ''}`;
        return (
          <MatrixCell
            key={bfId}
            value={cell.value}
            levelClass={cell.levelClass}
            extraClass={extra}
            tooltip={cell.tooltip}
          />
        );
      })}
    </>
  );
}

export type { DeckMatrixProps };
